import { useHistoryWithReferrer } from '@v2/utils/history';

import { IProduct } from '@interfaces/_cart';

import Clickable from '@components/common/presentational/clickables/default';
import { useAddCartModal, useAgeBundlePDPModal } from '@components/common/presentational/modal/common';
import { VarietyPackQuantityInput } from '@components/common/products/quantity';
import { useIsSwapping, useAddVarietyPackAction, useBuyingForBusiness } from '@components/common/products/utils';
import { setScrollPosition } from '@components/common/helpers/scroll-position';

import { apiImageSource } from '@v2/utils/http';
import { WPImage } from '@v2/utils/image';
import { useGTMItemDispatch } from '@v2/utils/gtm';
import { useEntity } from '@app/store/rtk.entities.selectors';
import { calculateBundleTotal } from '@v2/utils/cart';
import { useCartAllBoxes, usePackBoxIndex } from '@app/store/cart.selectors';
import Tooltip from '@v2/components/common/presentational/tooltip/tooltip';

export default function VarietyPack({ product, disabled, subscribed, staticPDP }: { product: IProduct, disabled: boolean, subscribed?: boolean, staticPDP?: boolean, }) {
    const history = useHistoryWithReferrer();
    const gtmDispatch = useGTMItemDispatch();
    const addVarietyPackAction = useAddVarietyPackAction();
    const blueprints = useEntity('blueprints');
    const bundleTotal = calculateBundleTotal(product, true);
    const bundleDiscountTotal = calculateBundleTotal(product);
    const packBoxIndex = usePackBoxIndex(product.id);
    const boxes = useCartAllBoxes();
    const isSwapping = useIsSwapping();

    const [showBundlePDP] = useAgeBundlePDPModal(product, true);
    const [showCartModal] = useAddCartModal();
    const {showBuyingForBusinessModal, buyingMoreThanTen} = useBuyingForBusiness();

    const onVarietyPackAdd = () => {
        if (buyingMoreThanTen) {
            showBuyingForBusinessModal();
        } else {
            addVarietyPackAction(product, blueprints, subscribed);

            if (!isSwapping) {
                showCartModal();
            }
        }
    }

    const select = () => {
        gtmDispatch('select_item', product);

        if (history.location.pathname === '/shop') {
            setScrollPosition('shop');
        }

        if (staticPDP || isSwapping) {
            showBundlePDP();
            return;
        }

        history.pushWithReferrer('/bundle/variety/' + product.slug)
    };

    return (
        <div className={`product ${product.availability === 'outofstock' ? 'out-of-stock' : (product.isBestSeller ? 'recommended' : '')}`}>
            <div className="product-link horizontal">
                <div style={{ position: 'relative' }}>
                    <Clickable onClick={select}>
                        <div className="image">
                            <WPImage src={product.hash ? apiImageSource(`bundle/${product.id}/${product.hash}`) : product.mainImage}
                            alt={product.mainImageAlt || product.title} />
                        </div>
                    </Clickable>
                </div>
                <div className="title">
                    <Clickable onClick={select}>{product.title}</Clickable>
                    <div className="quantity mobile">{product.size} items</div>
                </div>
                {(bundleTotal > 0 && bundleDiscountTotal > 0) &&
                    <div className="info">
                        {subscribed
                            ?
                            <>
                                <span className="regular">${bundleTotal.toFixed(2)}</span>
                                <Tooltip
                                    showOnClick={true}
                                    className="tooltip"
                                    width={250}
                                    description={`Optional Subscribe & Save for an additional 15% off`}>
                                    <span className="discount">${bundleDiscountTotal.toFixed(2)}</span>
                                </Tooltip>
                            </>
                            :
                            <span className="default">${bundleTotal.toFixed(2)}</span>
                        }
                        <span className="desktop"> | {product.size} items</span>
                    </div>
                }
            </div>
            {boxes[packBoxIndex]
                ?
                <div className="variety-pack-quantity">
                    <VarietyPackQuantityInput box={boxes[packBoxIndex]} />
                </div>
                :
                <div className={`action add ${(disabled || product?.status === 'outofstock') ? 'd-none' : ''}`}>
                    <button
                        className="button primary blue"
                        onClick={onVarietyPackAdd}
                        aria-label={'Add to bag'}
                        disabled={(disabled || product?.status === 'outofstock')}>
                        {isSwapping ? 'Select' : 'Add to bag'}
                    </button>
                </div>
            }
            <div className="out-of-stock-info">out of stock!</div>
            <div className="recommended-info">Best seller</div>
        </div>
    );
}